/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {InputComponent} from '../input/input.component';
import {UntypedFormBuilder, ValidationErrors, ValidatorFn} from '@angular/forms';
import {
    NgbCalendar,
    NgbDate,
    NgbDatepickerConfig,
    NgbDatepickerNavigateEvent,
    NgbInputDatepicker
} from '@ng-bootstrap/ng-bootstrap';
import {DatepickerHelperService} from '../../utils/datepicker-helper/datepicker-helper.service';
import {WarningsEnum} from '../../warnings/warnings.enum';
import moment from 'moment-timezone';

/**
 * <p>Service that handles the input date.
 * <p>Deprecated, use {@link InputDatepickerComponent}. Delete this as soon as there is no more references to it.
 * @deprecated
 * <p><b>NOTE:</b> using this and {@link InputDatepickerComponent} at the same time will create conflicts regarding the datepicker. Avoid using both.
 */
@Component({
    selector: 'app-input-date',
    templateUrl: './input-date.component.html',
    styleUrls: [
        '../input/input.component.css',
        './input-date.component.css'
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class InputDateComponent extends InputComponent implements OnInit, OnDestroy, OnChanges {
    @Input() displayMonths = 1;
    @Input() minDate: NgbDate = null;
    @Input() maxDate: NgbDate = null;
    @Input() initialValue: NgbDate | number;
    @Input() dayTemplateData: NgbDate;
    @Input() placeholder = 'dd-mm-yyyy';
    @Input() expiringDate: boolean;
    @Input() timezone: string;
    @Input() animationStyle: { [key: string]: any };
    @Input() isDisabledCalendarButton: boolean;
    @Output() closedDatepicker = new EventEmitter();
    @ViewChild('datepicker') datepicker: NgbInputDatepicker;
    WarningsEnum = WarningsEnum;

    /**
     * @todo CTWILS-3963 - This is a state variable, fix this on the identified task.
     */
    private currentMonth: {
        year: number;
        month: number;
    };

    constructor(protected formBuilder: UntypedFormBuilder,
                public datepickerHelper: DatepickerHelperService,
                private datepickerConfig: NgbDatepickerConfig,
                private calendar: NgbCalendar) {
        super(formBuilder);
        /** Datepicker configuration */
        this.datepickerConfig.firstDayOfWeek = 7;
        this.datepickerConfig.outsideDays = 'collapsed';
        this.datepickerConfig.navigation = 'arrows';
        this.datepickerConfig.maxDate = this.maxDate;
        this.datepickerConfig.minDate = this.minDate;
    }

    get datepickerCypressTag(): string {
        return `${this.baseCypressTag}-datepicker`;
    }

    get inputDateStyle(): { [key: string]: any } {
        let inputDateStyle = this.inputHeight;
        if (this.animationStyle != null) {
            inputDateStyle = {...this.animationStyle, ...this.inputHeight, ...this.isInvalidStyle};
        }
        return inputDateStyle;
    }

    get isInvalidStyle(): { [key: string]: any } {
        if (!this.isValid && this.animationStyle != null) {
            return {'color': '#d20000'};
        }
    }

    get todayDate(): NgbDate {
        let isToday: NgbDate;
        if (this.timezone != null) {
            isToday = this.datepickerHelper.convertTimestampTimeZoneToNgbDate(this.timezone);
        } else {
            isToday = this.calendar.getToday();
        }
        return isToday;
    }

    retrieveTemplateData = (): NgbDate | null => this.dayTemplateData ? this.dayTemplateData : null;

    ngOnInit(): void {
        super.ngOnInit();
        this.setInputValidator(this.datepickerValidator);

        this.subscriptions.add(
                this.inputControl.valueChanges.subscribe((value) => {
                    if (this.inputControl.valid) {
                        this.datepicker.navigateTo(value);
                    }
                }));
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.displayMonthsChangeHandler(changes.displayMonths);

        if (changes['minDate']) {
            this.datepickerConfig.minDate = changes['minDate'].currentValue;
        }

        if (changes['maxDate']) {
            this.datepickerConfig.maxDate = changes['maxDate'].currentValue;
        }
    }

    datepickerValidator: ValidatorFn = (): ValidationErrors | null => {
        if (this.inputControl == null) {
            return null;
        }
        const value = this.inputControl.value;
        if (value == null || value === ''
                || value.constructor.name === NgbDate.name
                || (value.constructor.name === Object.name && Object.name !== 'String')) {
            return null;
        }
        return {'incorrect': true};
    };

    focusInput(event: any): void {
        this.inputFocus.emit(event);
        this.datepicker.open();
    }

    clickInput(event: any): void {
        this.inputClick.emit(event);
        this.datepicker.open();
    }

    navigateHandler(event: NgbDatepickerNavigateEvent): void {
        if (event?.current != null && event?.next != null) {
            this.currentMonth = event.next;
        }
    }

    convertTimestampToDateFormat(timestamp: number, timezone?: string): string {
        let date = null;
        if (timestamp != null) {
            date = timezone != null ? moment(timestamp)
                            .tz(timezone)
                            .format('DD-MM-YYYY')
                    : moment(timestamp)
                            .format('DD-MM-YYYY');
        }
        return date;
    }

    private displayMonthsChangeHandler(displayMonthsChange: SimpleChange): void {
        if (displayMonthsChange != null && this.datepicker != null) {
            this.datepicker.displayMonths = displayMonthsChange.currentValue;

            // The NgbDatepicker doesn't support refresh, so we refresh it manually.
            if (this.datepicker.isOpen()) {
                this.datepicker.close();
                this.datepicker.open();
                this.datepicker.navigateTo(this.currentMonth);
            }
        }
    }
}
